import React, { type ChangeEvent, useEffect, useRef, useState } from 'react';

import { Hidden } from 'react-grid-system';
import TextareaAutosize from 'react-textarea-autosize';

import CharacterCounter from '../../CharacterCounter/CharacterCounter';
import EditIndicator from '../../EditIndicator/EditIndicator';
import AddIcon from '../../icons/add-icon-purple.svg?react';

import './_YatLinkInput.scss';

type Props = {
    value: string;
    placeholder: string;
    variant: 'title' | 'desc' | 'url' | 'icon';
    onChange?: (e: ChangeEvent<HTMLTextAreaElement>) => void;
    disabled?: boolean;
    maxLength?: number;
    debounce: number;
    isInvalid?: boolean;
    setUrlFocused?: (urlFocused: boolean) => void;
    maxRows: number;
};

function YatLinkInput({
    placeholder,
    variant,
    onChange,
    disabled,
    maxLength = 225,
    value,
    debounce,
    isInvalid,
    setUrlFocused,
    maxRows,
}: Props) {
    const [displayPlaceholder, setDisplayPlaceholder] = useState<string>(placeholder);
    const [isFocused, setIsFocused] = useState<boolean>(false);
    const [currentCharCount, setCurrentCharCount] = useState<number>(0);
    const [showIndicator, setShowIndicator] = useState<boolean>(false);
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    useEffect(() => {
        if (placeholder) {
            setDisplayPlaceholder(placeholder);
        }
    }, [placeholder]);

    function handleFocus(): void {
        setIsFocused(true);
        setUrlFocused?.(true);
        clearTimeout(debounce);
    }

    function handleBlur(): void {
        setDisplayPlaceholder(placeholder);
        setIsFocused(false);
        setUrlFocused?.(false);
    }

    function handleEnterPress(e: React.KeyboardEvent<HTMLTextAreaElement>): void {
        e.stopPropagation();
        e.preventDefault();
        e.currentTarget.blur();
    }

    useEffect(() => {
        if (value && value.length > 0) {
            setCurrentCharCount(value.length);
        }
    }, [value]);

    const showAddIcon = !value && !isFocused && variant === 'desc'; // because the placeholder bg image did not work. only show add icon if there's no value, it's not focused, and it's the description input
    return (
        <div className={`YatLinkInput ${disabled ? 'clickable' : 'editable'}`}>
            <div className="indicator-holder">
                {isFocused && variant !== 'url' && (
                    <CharacterCounter maxChars={maxLength} currentChars={currentCharCount} />
                )}
                {!isFocused && (
                    <Hidden xs sm>
                        <EditIndicator show={showIndicator} />
                    </Hidden>
                )}
            </div>
            <div className={`flex-holder ${variant}`}>
                {showAddIcon && <AddIcon />}
                <TextareaAutosize
                    className={`YatLinkInput--input  ${isInvalid ? 'invalid' : ''} ${variant}
                    `}
                    ref={textareaRef}
                    onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                            handleEnterPress(e);
                        }
                    }}
                    rows={maxRows}
                    onMouseEnter={() => setShowIndicator(true)}
                    onMouseLeave={() => setShowIndicator(false)}
                    placeholder={displayPlaceholder}
                    onChange={onChange}
                    disabled={disabled}
                    onFocus={() => handleFocus()}
                    onBlur={() => handleBlur()}
                    maxLength={maxLength}
                    value={value}
                    spellCheck="false"
                />
            </div>
        </div>
    );
}

export default YatLinkInput;
